class HeaderSearch extends HTMLElement {
  constructor() {
    super();

    this.searchButton = this.querySelector("[data-search-button]");
    this.searchForm = this.querySelector(".header__search");
    this.searchScroll = this.querySelector(".header-search__scroll");
    this.searchFormClose = this.querySelector("[data-search-form-close]");
    this.header = this.closest(".header");
    this.promoBar = document.querySelector(".promo-bar");

    this.updateSearchFormKeyboardInset =
      this.updateSearchFormKeyboardInset.bind(this);
    this.preventBackgroundTouchMove =
      this.preventBackgroundTouchMove.bind(this);

    this.searchButton.addEventListener("click", () => {
      if (this.searchForm.classList.contains("active")) {
        this.closeSearchForm();
      } else {
        this.openSearchForm();
      }
    });

    this.searchFormClose?.addEventListener("click", () => {
      this.closeSearchForm();
    });

    this.addEventListener("header-search:close", () => this.closeSearchForm());
    document.addEventListener(
      "mobile-search:open",
      this.handleMobileSearchOpen.bind(this),
    );

    window.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.closeSearchForm();
      }
    });

    this.windowSizeWatcher = this.onWindowThreshold(
      window.themeBreakpoints["tablet-wide"],
      () => this.closeSearchForm(true),
    );

    this.closeEsc = (e) => {
      if (e.key === "Escape" && this.searchForm.classList.contains("active")) {
        this.closeSearchForm();
      }
    };

    document.addEventListener("keydown", this.closeEsc);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener(
      "handleMobileSearchOpen",
      this.handleMobileSearchOpen,
    );
    this.windowSizeWatcher();
  }

  handleMobileSearchOpen() {
    if (this.searchButton.closest(".header__below-left")) {
      this.openSearchForm();
    }
  }

  openSearchForm() {
    if (this.searchForm.classList.contains("active")) return;

    window.lockScroll(this);

    const barHeight = this.promoBar?.getBoundingClientRect().height || 0;
    const headerHeight = this.header.getBoundingClientRect().height;

    this.searchForm.style.setProperty(
      "--search-form-max-height",
      `calc(100vh - ${barHeight}px)`,
    );

    this.searchForm.style.display = "block";
    if (this._isMobile) {
      window.openMobileMenuFullDimmer(this);
    } else {
      window.openDimmer(this);
    }
    this.header && this.header.classList.add("search-is-opened");

    this.updateSearchFormKeyboardInset();
    window.visualViewport?.addEventListener(
      "resize",
      this.updateSearchFormKeyboardInset,
    );
    this.searchForm.addEventListener(
      "touchmove",
      this.preventBackgroundTouchMove,
      { passive: false },
    );

    window.requestAnimationFrame(() => {
      this.searchForm.classList.add("active");
      window.trapFocus(
        this.searchForm,
        this.searchForm.querySelector('input[name="q"]'),
      );
    });
  }

  closeSearchForm(force = false) {
    if (!this.searchForm.classList.contains("active")) return;

    window.unlockScroll(this);
    window.visualViewport?.removeEventListener(
      "resize",
      this.updateSearchFormKeyboardInset,
    );
    this.searchForm.removeEventListener(
      "touchmove",
      this.preventBackgroundTouchMove,
    );

    const handleClose = () => {
      this.searchForm.style.display = "none";
      this.querySelector("live-search-wrapper")?.dispatchEvent(
        new CustomEvent("live-search-wrapper:clear"),
      );
      window.removeTrapFocus(this.searchButton, this.searchForm);
    };

    if (force) {
      handleClose();
    } else {
      this.searchForm.addEventListener("transitionend", () => handleClose(), {
        once: true,
      });
    }

    this.searchForm.classList.remove("active");
    window.closeMobileMenuFullDimmer(this);
    window.closeDimmer(this);
    this.header && this.header.classList.remove("search-is-opened");
  }

  get _isMobile() {
    return window.matchMedia("(max-width: 1023px)").matches;
  }

  updateSearchFormKeyboardInset() {
    if (!window.visualViewport) return;

    const inset = Math.max(
      0,
      window.innerHeight - window.visualViewport.height,
    );

    this.searchScroll?.style.setProperty(
      "--search-form-keyboard-inset",
      `${inset}px`,
    );
  }

  preventBackgroundTouchMove(event) {
    const scrollable = this.searchScroll;

    if (scrollable && scrollable.scrollHeight > scrollable.clientHeight) {
      return;
    }

    event.preventDefault();
  }

  onWindowThreshold(threshold, callback) {
    const mediaQuery = window.matchMedia(`(min-width: ${threshold}px)`);

    mediaQuery.addEventListener("change", callback);

    return () => mediaQuery.removeEventListener("change", callback);
  }
}

if (!customElements.get("header-search")) {
  customElements.define("header-search", HeaderSearch);
}
